import React, { useState, useCallback } from 'react';
import {
    AuthenticatedActions,
    AuthenticatedUserAction,
    FavoriteItem,
} from 'types';
import { HeartFilled, HeartOutlined, IconProps } from '@landr/maestro';
import { t } from '@lingui/macro';
import { usePrivateAction } from 'hooks/usePrivateAction';
import { useUpdateItemFavoriteStatus } from 'hooks/useUpdateItemFavoriteStatus';

const useFavoriteButton = ({
    item,
    onClick,
    label,
}: {
    item: FavoriteItem;
    onClick?: (event: React.MouseEvent) => void;
    label?: string;
}): {
    handleClick: (event: React.MouseEvent) => void;
    isFavorited: boolean;
    icon: React.FC<IconProps>;
    labelText: string;
} => {
    const [isFavorited, setIsFavorited] = useState(item.isFavorited ?? false);
    const { updateFavorite } = useUpdateItemFavoriteStatus();

    const doPrivateAction = usePrivateAction();

    const handleClick = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();

            doPrivateAction({
                handleAuthenticated: () => {
                    setIsFavorited(!isFavorited);
                    updateFavorite({ ...item, isFavorited });
                },
                actionDescription: {
                    type: AuthenticatedActions.updateItemFavoriteState,
                    description: t`add to favorites`,
                    args: [item],
                } as AuthenticatedUserAction<AuthenticatedActions.updateItemFavoriteState>,
            });

            onClick && onClick(event);
        },
        [
            setIsFavorited,
            isFavorited,
            item,
            onClick,
            updateFavorite,
            doPrivateAction,
        ],
    );

    return {
        handleClick,
        isFavorited,
        icon: isFavorited ? HeartFilled : HeartOutlined,
        labelText: label || t`Favorite`,
    };
};

export default useFavoriteButton;
